import React from "react";
import { Link } from "react-router-dom";
import {
  Compass, HandHeart, Sparkles, Tent, MapPin, Feather, ArrowRight,
} from "lucide-react";
import { EditableText } from "@/components/cms/CmsText";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Six reasons — composed by the Xaluca brand voice. Trilingual.
const REASONS = [
  {
    key: "family",
    icon: HandHeart,
    image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=900&q=80",
    stat: "1998",
    statLabel: { en: "Founded", fr: "Fondé", es: "Fundado" },
    title: { en: "A family, not a chain", fr: "Une famille, pas une chaîne", es: "Una familia, no una cadena" },
    body: {
      en: "Owned and run by two families — Catalan and Berber — since 1998. Every welcome is signed by a person who lives here, not by a logo printed in a head office.",
      fr: "Possédé et géré par deux familles — catalane et berbère — depuis 1998. Chaque accueil est signé par quelqu'un qui vit ici, pas par un logo imprimé au siège.",
      es: "Propiedad y gestión de dos familias — catalana y bereber — desde 1998. Cada bienvenida la firma alguien que vive aquí, no un logo impreso en una oficina central.",
    },
  },
  {
    key: "desert",
    icon: Tent,
    image: "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=900&q=80",
    stat: "04",
    statLabel: { en: "Desert camps", fr: "Camps du désert", es: "Campamentos" },
    title: { en: "Bivouacs no chain can replicate", fr: "Des bivouacs qu'aucune chaîne ne sait copier", es: "Vivacs que ninguna cadena puede igualar" },
    body: {
      en: "From a candle-lit Sahrawi haima to a sommelier-served suite tent — the only privatised, family-run camps inside Erg Chebbi with a sommelier, a butler and a five-course menu under the stars.",
      fr: "De la haïma sahraouie aux chandelles à la tente-suite servie par un sommelier — les seuls camps familiaux privatisés au cœur de l'Erg Chebbi avec sommelier, majordome et menu cinq services sous les étoiles.",
      es: "Desde una haima saharaui a la luz de las velas hasta una tienda-suite servida por sumiller — los únicos campamentos familiares privatizados en el Erg Chebbi con sumiller, mayordomo y menú de cinco tiempos bajo las estrellas.",
    },
  },
  {
    key: "geography",
    icon: MapPin,
    image: "https://images.unsplash.com/photo-1570133435536-7ececf000ef6?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    stat: "09",
    statLabel: { en: "Addresses", fr: "Adresses", es: "Direcciones" },
    title: { en: "From Sahara to the High Atlas", fr: "Du Sahara au Haut Atlas", es: "Del Sáhara al Alto Atlas" },
    body: {
      en: "Nine properties stitching the south together — desert kasbahs, a UNESCO-ksar guesthouse, lakeside cedar, a mountain refuge. One journey, one host, every landscape.",
      fr: "Neuf adresses qui cousent le sud ensemble — kasbahs du désert, maison d'hôte au cœur d'un ksar UNESCO, cèdre au bord du lac, refuge de montagne. Un seul voyage, un seul hôte, tous les paysages.",
      es: "Nueve propiedades que cosen el sur — kasbahs del desierto, hospedería en un ksar UNESCO, cedro junto al lago, refugio de montaña. Un viaje, un anfitrión, todos los paisajes.",
    },
  },
  {
    key: "concierge",
    icon: Feather,
    image: "https://images.unsplash.com/photo-1542401886-65d6c61db217?auto=format&fit=crop&w=900&q=80",
    stat: "24h",
    statLabel: { en: "Hand-written reply", fr: "Réponse à la main", es: "Respuesta a mano" },
    title: { en: "No booking engine — a real host", fr: "Pas de moteur de réservation — un hôte réel", es: "Sin motor de reservas — un anfitrión real" },
    body: {
      en: "Every enquiry is read and answered by a person within 24 hours. We propose a route, a price, a quiet recommendation — and we keep adjusting it until it fits the trip you're dreaming.",
      fr: "Chaque demande est lue et répondue à la main sous 24 heures. Nous proposons un itinéraire, un prix, une recommandation discrète — et nous ajustons jusqu'à ce que cela colle au voyage dont vous rêvez.",
      es: "Cada solicitud la lee y responde una persona en menos de 24 horas. Proponemos una ruta, un precio, una recomendación discreta — y la ajustamos hasta que encaje con el viaje que sueña.",
    },
  },
  {
    key: "local",
    icon: Compass,
    image: "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?crop=entropy&cs=srgb&fm=jpg&w=900&q=80",
    stat: "100%",
    statLabel: { en: "Local crews", fr: "Équipes locales", es: "Equipos locales" },
    title: { en: "Built by the south, for the south", fr: "Bâti par le sud, pour le sud", es: "Construido por el sur, para el sur" },
    body: {
      en: "Adobe walls, hand-laid zellige, fossil marble from local mines, Berber rugs woven a few valleys away. 300 families employed by the group — almost nothing in our hotels comes from outside the region.",
      fr: "Murs en adobe, zellige posé main, marbre fossile des mines voisines, tapis berbères tissés à quelques vallées d'ici. 300 familles employées par le groupe — presque rien dans nos hôtels ne vient de l'extérieur de la région.",
      es: "Muros de adobe, zellige colocado a mano, mármol fósil de las minas vecinas, alfombras bereberes tejidas a unos valles. 300 familias empleadas por el grupo — casi nada en nuestros hoteles viene de fuera de la región.",
    },
  },
  {
    key: "rituals",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=900&q=80",
    stat: "12h",
    statLabel: { en: "Mechoui", fr: "Méchoui", es: "Mechoui" },
    title: { en: "Rituals you'll only find here", fr: "Des rituels qu'on ne trouve qu'ici", es: "Rituales que solo encontrará aquí" },
    body: {
      en: "Twelve-hour mechoui sealed in a clay oven, hammam carved into a Berber bathhouse, fire-circle dinners under the Milky Way — small ceremonies our crews have practised across three decades.",
      fr: "Méchoui scellé douze heures dans un four d'argile, hammam taillé dans un bain berbère, dîners en cercle de feu sous la Voie lactée — petites cérémonies que nos équipes pratiquent depuis trois décennies.",
      es: "Mechoui sellado doce horas en horno de barro, hammam tallado en baño bereber, cenas en círculo de fuego bajo la Vía Láctea — pequeñas ceremonias que nuestras tripulaciones llevan tres décadas practicando.",
    },
  },
];

const WhyBookWithUs = ({ lang }) => {
  return (
    <section
      className="relative bg-[#1A1513] text-[#FDFBF7] py-24 md:py-32 lg:py-40 overflow-hidden"
      data-testid="section-why-book-with-us"
    >
      {/* Subtle Berber cross-lattice texture behind everything */}
      <div className="absolute inset-0 berber-bg-cross opacity-[0.06] pointer-events-none" aria-hidden="true" />
      {/* Film grain for editorial 35mm feel */}
      <span className="film-grain" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* HEADER */}
        <div className="grid md:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6} />
              {pick(
                { en: "Why book with us", fr: "Pourquoi réserver chez nous", es: "Por qué reservar con nosotros" },
                lang
              )}
            </div>
            <h2
              className="mt-5 font-serif-x text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
              data-testid="why-book-title"
            >
              <EditableText
                slot="home.why-book.title"
                defaults={{
                  en: "Six reasons the south feels different when it's Xaluca.",
                  fr: "Six raisons pour lesquelles le sud n'est pas le même chez Xaluca.",
                  es: "Seis razones por las que el sur se siente distinto cuando es Xaluca.",
                }}
                multiline
              />
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-3">
            <p className="text-base md:text-lg leading-relaxed text-[#FDFBF7]/75">
              <EditableText
                slot="home.why-book.subtitle"
                defaults={{
                  en: "Anyone can sell you a night in the dunes. What we offer is a hand-stitched stay across the south of Morocco — composed by a family that has spent twenty-seven years learning how to welcome.",
                  fr: "Tout le monde peut vous vendre une nuit dans les dunes. Nous proposons un séjour cousu à la main à travers le sud du Maroc — composé par une famille qui a passé vingt-sept ans à apprendre à accueillir.",
                  es: "Cualquiera puede venderle una noche en las dunas. Nosotros ofrecemos una estancia cosida a mano por el sur de Marruecos — compuesta por una familia que ha pasado veintisiete años aprendiendo a acoger.",
                }}
                multiline
              />
            </p>
          </div>
        </div>

        {/* REASONS GRID — six tall editorial tiles, alternating hover-accent gold */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#FDFBF7]/10 border border-[#FDFBF7]/10"
          data-testid="why-book-reasons-grid"
        >
          {REASONS.map((r) => {
            const Icon = r.icon;
            return (
              <article
                key={`why-${r.key}`}
                className="group relative overflow-hidden bg-[#1A1513] hover:bg-[#221A16] transition-colors duration-500 p-8 md:p-10 lg:p-12 flex flex-col h-full"
                data-testid={`why-book-reason-${r.key}`}
              >

                <div className="relative z-10 flex items-start justify-between gap-6">
                  <div
                    className="w-12 h-12 rounded-full border border-[#D4A373]/40 bg-[#1A1513]/60 backdrop-blur-sm text-[#D4A373] flex items-center justify-center group-hover:bg-[#D4A373] group-hover:text-[#1A1513] transition-all duration-500"
                    aria-hidden="true"
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div className="text-right">
                    <span
                      className="font-serif-x text-3xl md:text-4xl text-[#D4A373] leading-none tabular-nums drop-shadow-[0_2px_8px_rgba(26,21,19,0.85)]"
                      data-testid={`why-book-stat-${r.key}`}
                    >
                      {r.stat}
                    </span>
                    <div className="mt-1.5 text-[9px] tracking-[0.3em] uppercase text-[#FDFBF7]/70 drop-shadow-[0_1px_4px_rgba(26,21,19,0.9)]">
                      {pick(r.statLabel, lang)}
                    </div>
                  </div>
                </div>

                <h3 className="relative z-10 mt-9 font-serif-x text-2xl md:text-[28px] leading-[1.08] text-[#FDFBF7]">
                  {pick(r.title, lang)}
                </h3>

                <p className="relative z-10 mt-5 text-[15px] leading-relaxed text-[#FDFBF7]/75 flex-1">
                  {pick(r.body, lang)}
                </p>

                <div className="relative z-10 mt-9 pt-5 border-t border-[#FDFBF7]/10">
                  <span
                    className="font-serif-x italic text-sm text-[#D4A373]/70 tracking-tight"
                    aria-hidden="true"
                  >
                    — {String(REASONS.indexOf(r) + 1).padStart(2, "0")} of {String(REASONS.length).padStart(2, "0")}
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        {/* BOTTOM CTA BAND */}
        <div
          className="mt-20 md:mt-24 grid md:grid-cols-12 gap-8 lg:gap-12 items-center border-t border-[#FDFBF7]/15 pt-14"
          data-testid="why-book-cta-band"
        >
          <div className="md:col-span-7">
            <p
              className="font-serif-x italic text-2xl md:text-3xl lg:text-4xl leading-snug text-[#FDFBF7]"
              data-testid="why-book-quote"
            >
              {pick(
                {
                  en: "“We don't have a booking engine. We have a family that picks up the phone, writes back, and remembers your name when you arrive.”",
                  fr: "« Nous n'avons pas de moteur de réservation. Nous avons une famille qui décroche le téléphone, vous répond, et se souvient de votre prénom à votre arrivée. »",
                  es: "«No tenemos motor de reservas. Tenemos una familia que coge el teléfono, le responde, y se acuerda de su nombre cuando llega.»",
                },
                lang
              )}
            </p>
            <p className="mt-5 text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
              {pick(
                {
                  en: "Tayeb & Lluís · Co-founders, Grup Xaluca",
                  fr: "Tayeb & Lluís · Cofondateurs, Grup Xaluca",
                  es: "Tayeb y Lluís · Cofundadores, Grup Xaluca",
                },
                lang
              )}
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col items-start md:items-end gap-4">
            <Link
              to="/booking"
              className="inline-flex items-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-[11px] tracking-[0.25em] uppercase transition-colors"
              data-testid="why-book-cta-book"
            >
              {pick(
                { en: "Start an enquiry", fr: "Démarrer une demande", es: "Iniciar una solicitud" },
                lang
              )}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </Link>
            <Link
              to="/packs"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#FDFBF7]/40 pb-1 text-[#FDFBF7] hover:border-[#D4A373] hover:text-[#D4A373] transition-colors"
              data-testid="why-book-cta-packs"
            >
              {pick(
                { en: "Browse curated packs", fr: "Voir les packs sur mesure", es: "Ver los packs curados" },
                lang
              )}
              <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyBookWithUs;
